import React, { memo, useState } from 'react';
import { motion } from 'framer-motion';
import { images as imageApi } from '../../utils/api';
import ImageViewerModal from '../shared/ImageViewerModal';

function CarouselItem({
  coin,
  image,
  imageIndex,
  isShared,
  isDragging,
  loadedImages,
  onImageClick,
  onImageLoad
}) {
  const imagePath = image?.file_path ? imageApi.getImage(image.file_path, image.source) : null;
  const isLoaded = loadedImages.has(imagePath);

  return (
    <motion.div
      className="flex-none h-[300px] sm:h-[500px] relative mx-2 sm:mx-4 cursor-pointer snap-center min-w-[200px] sm:min-w-[300px]"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ 
        opacity: 1, 
        scale: 1,
        x: isDragging ? null : 0
      }}
      whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
      onClick={() => !isDragging && onImageClick(coin.images, imageIndex, coin)}
      transition={{
        type: "spring",
        stiffness: 150,
        damping: 25
      }}
    >
      <motion.div className="w-full h-full flex items-center justify-center">
        {!imagePath || image?.isPlaceholder ? (
          <div className="w-full h-full flex items-center justify-center bg-gray-800">
            <img 
              src="/images/placeholder.svg" 
              alt="No image available"
              className="w-32 h-32 opacity-30"
            />
          </div>
        ) : (
          <div className="relative w-full h-full">
            {!isLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-white"></div>
              </div>
            )}
            <motion.img
              src={imagePath}
              alt={`${coin.year} ${coin.denomination} - Image ${imageIndex + 1}`}
              className={`w-full h-full object-cover transition-opacity duration-300 ${
                isLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={(e) => {
                const src = e.target.src;
                if (!loadedImages.has(src)) {
                  onImageLoad?.(src);
                }
              }}
              initial={false}
            />
          </div>
        )}
      </motion.div>
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm">
        {coin.year} {coin.denomination}
      </div>
    </motion.div>
  );
}

function PlaceholderItem({
  coin,
  isDragging,
  onImageClick
}) {
  return (
    <motion.div
      className="flex-none h-[300px] sm:h-[500px] relative mx-2 sm:mx-4 cursor-pointer snap-center min-w-[200px] sm:min-w-[300px]"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ 
        opacity: 1, 
        scale: 1,
        x: isDragging ? null : 0
      }}
      whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
      onClick={() => !isDragging && onImageClick([], 0, coin)}
      transition={{
        type: "spring",
        stiffness: 150,
        damping: 25
      }}
    >
      <motion.div className="w-full h-full flex items-center justify-center">
        <div className="w-full h-full flex items-center justify-center bg-gray-800">
          <img 
            src="/images/placeholder.svg" 
            alt="No image available"
            className="w-32 h-32 opacity-30"
          />
        </div>
      </motion.div>
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm">
        {coin.year} {coin.denomination}
      </div>
    </motion.div>
  );
}

const MemoizedCarouselItem = memo(CarouselItem);
const MemoizedPlaceholderItem = memo(PlaceholderItem);

function CarouselView({
  coins,
  isShared,
  loadedImages,
  isDragging,
  carouselRef,
  handleDragStart,
  handleDragMove,
  handleDragEnd,
  scrollCarousel,
  onImageLoad
}) {
  const [selectedImage, setSelectedImage] = useState(null);

  const handleImageClick = (images, index, coin) => {
    setSelectedImage({ images, index, coin });
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  const handleNavigate = (newIndex) => {
    setSelectedImage(prev => ({ ...prev, index: newIndex }));
  };

  if (!coins || coins.length === 0) {
    return (
      <>
        <div className="flex justify-center items-center h-[400px] sm:h-[600px] bg-black text-white">
          No coins available in this set
        </div>
      </>
    );
  }

  return (
    <>
      <motion.div
        className="relative w-full h-[400px] sm:h-[600px] bg-black"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Navigation Arrows */}
        <motion.button
          className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 rounded-full p-2 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          whileHover={{ scale: 1.1 }}
          onClick={() => scrollCarousel('left')}
          aria-label="Previous coin"
          title="Previous coin"
        >
          <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </motion.button>
        <motion.button
          className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 rounded-full p-2 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          whileHover={{ scale: 1.1 }}
          onClick={() => scrollCarousel('right')}
          aria-label="Next coin"
          title="Next coin"
        >
          <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </motion.button>

        <div 
          ref={carouselRef}
          className="flex items-center h-full px-2 sm:px-4 overflow-x-auto [&::-webkit-scrollbar]:h-2 
            [&::-webkit-scrollbar-track]:bg-white/10 
            [&::-webkit-scrollbar-thumb]:bg-white/30 
            [&::-webkit-scrollbar-thumb]:rounded-full 
            [&::-webkit-scrollbar-thumb:hover]:bg-white/40
            focus:outline-none snap-x snap-mandatory scroll-smooth"
          style={{
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch'
          }}
          tabIndex={0}
          role="region"
          aria-label="Coin carousel"
          onMouseDown={(e) => {
            e.preventDefault();
            handleDragStart(e);
          }}
          onMouseMove={(e) => {
            e.preventDefault();
            handleDragMove(e);
          }}
          onMouseUp={(e) => {
            e.preventDefault();
            handleDragEnd();
          }}
          onMouseLeave={(e) => {
            e.preventDefault();
            handleDragEnd();
          }}
          onTouchStart={() => {
            // Don't prevent default touch behavior on mobile
            handleDragEnd();
          }}
        >
          {coins.flatMap((coin) => {
            if (!coin.images || coin.images.length === 0) {
              return [(
                <MemoizedPlaceholderItem
                  key={`${coin.id}-placeholder`}
                  coin={coin}
                  isDragging={isDragging}
                  onImageClick={handleImageClick}
                />
              )];
            }

            return coin.images.map((image, imageIndex) => (
              <MemoizedCarouselItem
                key={`${coin.id}-${imageIndex}`}
                coin={coin}
                image={image}
                imageIndex={imageIndex}
                isShared={isShared}
                isDragging={isDragging}
                loadedImages={loadedImages}
                onImageClick={handleImageClick}
                onImageLoad={onImageLoad}
              />
            ));
          })}
        </div>
      </motion.div>
      {selectedImage && (
        <ImageViewerModal
          selectedImage={selectedImage}
          onClose={closeImageModal}
          onNavigate={handleNavigate}
        />
      )}
    </>
  );
}

export default memo(CarouselView);
